import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, API_BASE, logHttpError } from '../services/api';
import ReactionPicker from '../components/ReactionPicker';
import MediaViewer, { type MediaItem } from '../components/MediaViewer';
import EventCard from '../components/events/EventCard';
import PetCard from '../components/pet/PetCard';
import type { EventSummary } from '../events/types';

interface Comment {
  id: number;
  content: string;
  user_id: number;
  username: string;
  author_username?: string;
  author_avatar_url: string | null;
  created_at: string;
  parent_id: number | null;
  parent_author_username: string | null;
}

interface Post {
  id: number;
  content: string;
  file_url: string | null;
  file_type: string | null;
  user_id: number;
  clan_id: number;
  is_public: boolean;
  created_at: string;
  author_username: string;
  author_avatar_url: string | null; // <-- ДОБАВЛЕНО ПОЛЕ
  author_clan_name: string;
  likes_count: number;
  is_liked_by_me: boolean;
  comments: Comment[];
}

const uniqueEvents = (items: EventSummary[]) => Array.from(new Map(items.map((event) => [event.id, event])).values());

const QUICK_EMOJIS = ['😀', '😂', '', '😎', '🤔', '👍', '❤️', '', '🎉', '🦈'];

const FeedPage: React.FC = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('sharkly_user_id');

  const [activeTab, setActiveTab] = useState<'public' | 'clan'>('public');
  const [posts, setPosts] = useState<Post[]>([]);
  const [newPostText, setNewPostText] = useState('');
  const [postType, setPostType] = useState<'public' | 'clan'>('public');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [commentInputs, setCommentInputs] = useState<Record<number, string>>({});
  const [replyTo, setReplyTo] = useState<{ postId: number; commentId: number; username: string } | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [userClanId, setUserClanId] = useState<number>(1);
  const [mediaViewer, setMediaViewer] = useState<{ media: MediaItem[]; initialIndex: number } | null>(null);
  const [feedEvents, setFeedEvents] = useState<EventSummary[]>([]);
  const [eventsError, setEventsError] = useState<string | null>(null);

  // Состояния для медиа и эмодзи
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchUserData = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUserClanId(res.data.clan_id);
    } catch (err) {
      logHttpError("Ошибка загрузки данных пользователя", err);
    }
  }, [userId]);

  const fetchPosts = useCallback(async () => {
    if (!userId) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get(`/posts/?feed_type=${activeTab}`);
      const postsWithComments = await Promise.all(response.data.map(async (post: Post) => {
        const commentsResponse = await api.get(`/posts/${post.id}/comments`);
        return { ...post, comments: commentsResponse.data };
      }));
      setPosts(postsWithComments);
    } catch (err: unknown) {
      logHttpError("Ошибка загрузки ленты", err);
      const detail = typeof err === 'object' && err !== null && 'response' in err
        ? (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
        : undefined;
      setError(typeof detail === 'string' ? detail : "Не удалось загрузить ленту");
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, userId]);

  const fetchEvents = useCallback(async () => {
    try {
      setEventsError(null);
      const response = await api.get<EventSummary[]>('/events?state=active&limit=3');
      setFeedEvents(uniqueEvents(response.data).slice(0, 3));
    } catch (err) {
      logHttpError('Ошибка загрузки событий ленты', err);
      setEventsError('Не удалось загрузить события');
    }
  }, []);

  useEffect(() => {
    if (!userId) {
      navigate('/login');
      return;
    }
    void Promise.resolve().then(fetchUserData);
  }, [fetchUserData, navigate, userId]);

  useEffect(() => {
    if (userId) void Promise.resolve().then(fetchPosts);
  }, [fetchPosts, userId]);

  useEffect(() => {
    if (userId) void Promise.resolve().then(fetchEvents);
  }, [fetchEvents, userId]);

  const handleSendMessage = async () => {
    if ((!newPostText.trim() && !previewFile) || !userId) return;

    try {
      let fileUrl = null;
      let fileType = null;

      if (previewFile) {
        setUploadingFile(true);
        const formData = new FormData();
        formData.append('file', previewFile);

        const uploadRes = await api.post('/posts/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        fileUrl = uploadRes.data.file_url;
        fileType = uploadRes.data.file_type;
        setUploadingFile(false);
      }

      await api.post('/posts/',
        {
          content: newPostText,
          clan_id: userClanId,
          is_public: postType === 'public',
          file_url: fileUrl,
          file_type: fileType
        }
      );

      setNewPostText('');
      setPreviewFile(null);
      fetchPosts();
    } catch (err) {
      logHttpError("Ошибка создания поста", err);
      alert("Не удалось опубликовать пост");
      setUploadingFile(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setPreviewFile(file);
  };

  const insertEmoji = (emoji: string) => {
    setNewPostText(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const handleToggleLike = async (postId: number) => {
    try {
      await api.post(`/posts/${postId}/like`);
      setPosts(prev => prev.map(p => {
        if (p.id === postId) {
          return {
            ...p,
            is_liked_by_me: !p.is_liked_by_me,
            likes_count: p.is_liked_by_me ? p.likes_count - 1 : p.likes_count + 1
          };
        }
        return p;
      }));
    } catch (err) {
      logHttpError("Ошибка лайка", err);
    }
  };

  const handleAddComment = async (postId: number) => {
    const text = commentInputs[postId];
    if (!text?.trim() || !userId) return;

    try {
      const res = await api.post(
        `/posts/${postId}/comments`,
        {
          post_id: postId,
          content: text,
          parent_id: replyTo?.postId === postId ? replyTo.commentId : null
        }
      );

      setPosts(prev => prev.map(p => {
        if (p.id === postId) {
          return { ...p, comments: [...p.comments, res.data] };
        }
        return p;
      }));
      setCommentInputs(prev => ({ ...prev, [postId]: '' }));
      if (replyTo?.postId === postId) setReplyTo(null);
    } catch (err) {
      logHttpError("Ошибка комментария", err);
      alert("Не удалось отправить комментарий");
    }
  };

  const [editingPost, setEditingPost] = useState<number | null>(null);
  const [editText, setEditText] = useState('');

  const handleDeletePost = async (postId: number) => {
    if (!confirm('Удалить пост?')) return;
    try {
      await api.delete(`/posts/${postId}`);
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch (err) {
      logHttpError("Ошибка удаления поста", err);
    }
  };

  const handleEditPost = async (postId: number) => {
    if (!editText.trim()) return;
    try {
      await api.put(`/posts/${postId}`, { content: editText });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, content: editText } : p));
      setEditingPost(null);
    } catch (err) {
      logHttpError("Ошибка изменения поста", err);
    }
  };

  const handleDeleteComment = async (postId: number, commentId: number) => {
    if (!confirm('Удалить комментарий?')) return;
    try {
      await api.delete(`/posts/${postId}/comments/${commentId}`);
      setPosts(prev => prev.map(p => p.id === postId ? {
        ...p,
        comments: p.comments
          .filter(c => c.id !== commentId)
          .map(c => c.parent_id === commentId
            ? { ...c, parent_id: null, parent_author_username: null }
            : c
          )
      } : p));
      if (replyTo?.commentId === commentId) setReplyTo(null);
    } catch (err) {
      logHttpError("Ошибка удаления комментария", err);
    }
  };

  const handleEditComment = async (postId: number, commentId: number) => {
    if (!editingCommentText.trim()) return;
    try {
      await api.put(
        `/posts/${postId}/comments/${commentId}`,
        { content: editingCommentText }
      );
      setPosts(prev => prev.map(p => p.id === postId
        ? { ...p, comments: p.comments.map(c => c.id === commentId ? { ...c, content: editingCommentText } : c) }
        : p
      ));
      setEditingCommentId(null);
      setEditingCommentText('');
    } catch {
      alert('Не удалось изменить комментарий');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMins = Math.floor((now.getTime() - date.getTime()) / 60000);
    if (diffMins < 1) return 'Только что';
    if (diffMins < 60) return `${diffMins} мин назад`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} ч назад`;
    return date.toLocaleDateString('ru-RU');
  };

  // Функция получения аватарки (Реальная или Заглушка)
  const getAvatarUrl = (username: string, avatarUrl: string | null, size: number = 40) => {
    if (avatarUrl) {
      return `${API_BASE}${avatarUrl}`;
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(username)}&background=random&color=fff&size=${size}`;
  };

  const openMediaViewer = (postId: number) => {
    const feedMedia = posts.flatMap((post) => {
      if (!post.file_url || (post.file_type !== 'image' && post.file_type !== 'video')) return [];

      return [{
        type: post.file_type,
        url: `${API_BASE}${post.file_url}`,
        title: `Пост @${post.author_username || 'Аноним'}`,
      } satisfies MediaItem];
    });
    const initialIndex = posts
      .filter((post) => post.file_url && (post.file_type === 'image' || post.file_type === 'video'))
      .findIndex((post) => post.id === postId);

    if (initialIndex >= 0) setMediaViewer({ media: feedMedia, initialIndex });
  };

  // Рендер медиа внутри поста
  const renderPostMedia = (post: Post) => {
    if (!post.file_url) return null;
    const url = `${API_BASE}${post.file_url}`;

    if (post.file_type === 'image') {
      return (
        <div className="mt-3 w-fit max-w-full mx-auto rounded-xl overflow-hidden border border-gray-700 cursor-zoom-in" onClick={() => openMediaViewer(post.id)}>
          <img src={url} alt="Post attachment" className="block max-w-full h-auto object-contain rounded-xl" />
        </div>
      );
    }
    if (post.file_type === 'video') {
      return (
        <div className="relative mt-3 rounded-xl overflow-hidden border border-gray-700 bg-black">
          <video src={url} controls className="w-full max-h-[500px]" />
          <button
            type="button"
            onClick={() => openMediaViewer(post.id)}
            className="absolute right-3 top-3 rounded-lg bg-black/70 px-3 py-2 text-sm font-bold text-white transition hover:bg-black/90"
          >
            ⛶ Открыть видео
          </button>
        </div>
      );
    }
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="mt-3 flex items-center gap-3 p-3 bg-gray-800 rounded-xl hover:bg-gray-700 transition border border-gray-700">
        <span className="text-2xl">📄</span>
        <span className="text-sm font-medium">Скачать файл</span>
      </a>
    );
  };

  if (!userId) return null;

  return (
    <div className="min-h-screen bg-[#0f1115] text-white pb-20">
      <MediaViewer
        isOpen={mediaViewer !== null}
        onClose={() => setMediaViewer(null)}
        media={mediaViewer?.media ?? []}
        initialIndex={mediaViewer?.initialIndex ?? 0}
      />
      <main className="max-w-2xl mx-auto p-4">
        <div className="flex bg-[#1a1d24] p-1 rounded-xl mb-6 border border-gray-800 shadow-inner">
          <button onClick={() => setActiveTab('public')} className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all ${activeTab === 'public' ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}>🌍 Для всех</button>
          <button onClick={() => setActiveTab('clan')} className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-all ${activeTab === 'clan' ? 'bg-emerald-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}>🐺 Только стая</button>
        </div>

        {(feedEvents.length > 0 || eventsError) && (
          <section aria-label="Активные события" className="mb-8">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="text-lg font-black text-white">Активные события</h2>
              <button type="button" onClick={() => navigate('/events')} className="shark-accent-text text-sm font-bold hover:underline">Все события</button>
            </div>
            {feedEvents.length > 0 ? (
              <div className="grid gap-3 sm:grid-cols-3">
                {feedEvents.map((event) => <EventCard key={event.id} event={event} compact onOpen={(eventId) => navigate(`/events?event=${eventId}`)} />)}
              </div>
            ) : (
              <div className="shark-card shark-border rounded-2xl border p-4"><p className="shark-muted text-sm">{eventsError}</p><button type="button" onClick={fetchEvents} className="shark-accent-text mt-2 text-sm font-bold hover:underline">Повторить загрузку событий</button></div>
            )}
          </section>
        )}

        <div className="mb-8">
          <PetCard />
        </div>

        {/* Форма создания поста */}
        <div className="bg-[#1a1d24] rounded-2xl p-4 mb-8 border border-gray-800 shadow-lg">
          <textarea
            value={newPostText}
            onChange={(e) => setNewPostText(e.target.value)}
            placeholder={activeTab === 'clan' ? "Секретное сообщение для стаи..." : "О чем думаешь, боец?"}
            className="w-full bg-transparent text-white placeholder-gray-500 resize-none outline-none min-h-[80px]"
          />

          {/* Предпросмотр файла */}
          {previewFile && (
            <div className="mt-2 p-2 bg-gray-900/50 rounded-lg flex items-center justify-between border border-gray-700">
              <div className="flex items-center gap-2">
                {previewFile.type.startsWith('image/') ? (
                  <img src={URL.createObjectURL(previewFile)} alt="Preview" className="w-10 h-10 rounded object-cover" />
                ) : (
                  <span className="text-xl">📎</span>
                )}
                <span className="text-xs text-gray-300 truncate max-w-[150px]">{previewFile.name}</span>
              </div>
              <button onClick={() => setPreviewFile(null)} className="text-gray-400 hover:text-red-400">✕</button>
            </div>
          )}

          {/* Панель эмодзи */}
          {showEmojiPicker && (
            <div className="mt-2 p-2 bg-gray-900 rounded-lg grid grid-cols-5 gap-2 border border-gray-700 animate-fade-in">
              {QUICK_EMOJIS.map(emoji => (
                <button key={emoji} onClick={() => insertEmoji(emoji)} className="text-xl hover:bg-gray-700 rounded p-1 transition">{emoji}</button>
              ))}
            </div>
          )}

          <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-700/50">
            <div className="flex gap-2 items-center">
              <button onClick={() => setShowEmojiPicker(!showEmojiPicker)} className={`p-2 rounded-full transition ${showEmojiPicker ? 'bg-yellow-500/20 text-yellow-400' : 'hover:bg-gray-700 text-gray-400'}`}>😀</button>
              <button onClick={() => fileInputRef.current?.click()} disabled={uploadingFile} className="p-2 rounded-full hover:bg-gray-700 text-gray-400 transition">
                {uploadingFile ? '' : '📎'}
              </button>
              <input type="file" ref={fileInputRef} className="hidden" accept="image/*,video/*,.pdf,.doc,.zip" onChange={handleFileSelect} disabled={uploadingFile} />

              <div className="h-6 w-px bg-gray-700 mx-1"></div>

              <button onClick={() => setPostType('public')} className={`text-xs px-3 py-1.5 rounded-full border transition ${postType === 'public' ? 'bg-blue-500/20 border-blue-500 text-blue-400' : 'border-gray-600 text-gray-500'}`}>🌍 Публично</button>
              <button onClick={() => setPostType('clan')} className={`text-xs px-3 py-1.5 rounded-full border transition ${postType === 'clan' ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' : 'border-gray-600 text-gray-500'}`}> Для стаи</button>
            </div>
            <button
              onClick={handleSendMessage}
              disabled={(!newPostText.trim() && !previewFile) || uploadingFile}
              className={`px-6 py-2 rounded-full font-bold text-sm transition-all ${
                (newPostText.trim() || previewFile) && !uploadingFile
                ? 'bg-blue-600 hover:bg-blue-500 shadow-[0_0_15px_rgba(37,99,235,0.4)]' 
                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              {uploadingFile ? 'Загрузка...' : 'Опубликовать'}
            </button>
          </div>
        </div>

        {/* Лента постов */}
        <div className="space-y-6">
          {isLoading ? (
            <div className="text-center py-20">
              <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-500">Загружаем ленту...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20 bg-red-500/10 border border-red-500/30 rounded-2xl p-6">
              <p className="text-red-400 font-bold mb-2">Ошибка!</p>
              <p className="text-gray-300 text-sm mb-4">{error}</p>
              <button onClick={fetchPosts} className="px-4 py-2 bg-red-600 hover:bg-red-500 rounded-lg text-white text-sm font-bold">Попробовать снова</button>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20 bg-[#1a1d24] rounded-2xl border border-gray-800 border-dashed">
              <p className="text-4xl mb-3">🦈</p>
              <p className="text-gray-400">Лента пуста.</p>
            </div>
          ) : (
            posts.map((post) => (
              <div key={post.id} className="bg-[#1a1d24] rounded-2xl p-5 border border-gray-800 relative overflow-hidden group">
                <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-blue-500" />

                <div className="flex items-center justify-between mb-3 pl-3">
                  <div className="flex items-center gap-3">
                    {/* ИСПРАВЛЕНО: Теперь здесь img с проверкой на наличие аватарки */}
                    <img
                      src={getAvatarUrl(post.author_username, post.author_avatar_url, 40)}
                      alt={post.author_username}
                      className="w-10 h-10 rounded-full object-cover border border-gray-700 shrink-0 bg-[#0f1115]"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-sm text-white">{post.author_username || 'Аноним'}</h3>
                        {post.author_clan_name && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 border border-gray-700 uppercase">{post.author_clan_name}</span>}
                      </div>
                      <span className="text-xs text-gray-500">{formatDate(post.created_at)}</span>
                    </div>
                  </div>
                  {!post.is_public && <span className="text-emerald-400 text-[10px] font-bold flex items-center gap-1 bg-emerald-500/10 px-2.5 py-1 rounded-full border border-emerald-500/30 uppercase"> Стая</span>}
                  {post.user_id.toString() === userId && (
                    <div className="flex gap-1 ml-2">
                      <button onClick={() => { setEditingPost(post.id); setEditText(post.content); }} className="text-gray-600 hover:text-yellow-400 text-xs transition" title="Редактировать">✏️</button>
                      <button onClick={() => handleDeletePost(post.id)} className="text-gray-600 hover:text-red-400 text-xs transition" title="Удалить">🗑️</button>
                    </div>
                  )}
                </div>

                {editingPost === post.id ? (
                  <div className="pl-3 mb-2">
                    <textarea value={editText} onChange={e => setEditText(e.target.value)}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg p-2 text-white text-sm resize-none focus:border-blue-500 outline-none" rows={3} />
                    <div className="flex gap-2 mt-1">
                      <button onClick={() => handleEditPost(post.id)} className="text-xs px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded-lg">Сохранить</button>
                      <button onClick={() => setEditingPost(null)} className="text-xs px-3 py-1 text-gray-400 hover:text-white">Отмена</button>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-200 leading-relaxed pl-3 whitespace-pre-wrap mb-2">{post.content}</p>
                )}

                {/* Рендер медиа */}
                {renderPostMedia(post)}

                <div className="flex gap-6 pl-3 text-gray-500 text-sm mb-2 mt-4">
                  <button onClick={() => handleToggleLike(post.id)} className={`hover:text-pink-500 transition flex items-center gap-1.5 ${post.is_liked_by_me ? 'text-pink-500' : ''}`}>
                    <span>{post.is_liked_by_me ? '❤️' : '🤍'}</span> {post.likes_count}
                  </button>
                  <button onClick={() => document.getElementById(`comment-${post.id}`)?.focus()} className="hover:text-blue-400 transition flex items-center gap-1.5">💬 {post.comments.length}</button>
                </div>

                <div className="pl-3 mb-4">
                  <ReactionPicker entityType="post" entityId={post.id} />
                </div>

                {post.comments.length > 0 && (
                  <div className="pl-3 space-y-3 mb-4 border-l-2 border-gray-700 ml-1">
                    {(() => {
                      const commentIds = new Set(post.comments.map(comment => comment.id));
                      return post.comments.filter(comment => comment.parent_id === null || !commentIds.has(comment.parent_id)).map(parent => {
                      const replies = post.comments.filter(comment => comment.parent_id === parent.id);
                      const renderComment = (comment: Comment, isReply = false) => {
                        const username = comment.author_username || comment.username;
                        return (
                          <div key={comment.id} className={`text-sm flex items-start gap-2 group/comment ${isReply ? 'ml-5 mt-2 border-l border-gray-700 pl-3' : ''}`}>
                            <img src={getAvatarUrl(username, comment.author_avatar_url, 24)} className="w-6 h-6 rounded-full object-cover mt-0.5 shrink-0" alt="" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-gray-300">{username}</span>
                                <span className="text-[10px] text-gray-600">{formatDate(comment.created_at)}</span>
                                {comment.user_id.toString() === userId && (
                                  <>
                                    <button onClick={() => { setEditingCommentId(comment.id); setEditingCommentText(comment.content); }} className="text-gray-600 hover:text-yellow-400 text-[10px] opacity-0 group-hover/comment:opacity-100 transition">Изменить</button>
                                    <button onClick={() => handleDeleteComment(post.id, comment.id)} className="text-gray-600 hover:text-red-400 text-[10px] opacity-0 group-hover/comment:opacity-100 transition">Удалить</button>
                                  </>
                                )}
                              </div>
                              {editingCommentId === comment.id ? (
                                <div className="mt-1 flex gap-2">
                                  <input value={editingCommentText} onChange={event => setEditingCommentText(event.target.value)} onKeyDown={event => event.key === 'Enter' && handleEditComment(post.id, comment.id)} className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-2 py-1 text-sm text-white outline-none focus:border-blue-500" autoFocus />
                                  <button onClick={() => handleEditComment(post.id, comment.id)} className="text-xs text-blue-400 hover:text-blue-300">Сохранить</button>
                                  <button onClick={() => { setEditingCommentId(null); setEditingCommentText(''); }} className="text-xs text-gray-500 hover:text-white">Отмена</button>
                                </div>
                              ) : (
                                <div className="text-gray-400 text-sm">
                                  {comment.parent_author_username && <span className="text-blue-400 mr-1">@{comment.parent_author_username},</span>}
                                  {comment.content}
                                </div>
                              )}
                              {!isReply && (
                                <button onClick={() => setReplyTo({ postId: post.id, commentId: comment.id, username })} className="mt-1 text-[10px] text-gray-500 hover:text-blue-400">Ответить</button>
                              )}
                            </div>
                          </div>
                        );
                      };

                      return <div key={parent.id}>{renderComment(parent)}{replies.map(reply => renderComment(reply, true))}</div>;
                      });
                    })()}
                  </div>
                )}

                <div className="flex gap-2 pl-3 mt-2">
                  <div className="flex-1">
                    {replyTo?.postId === post.id && (
                      <div className="mb-1 text-xs text-blue-400 flex items-center justify-between px-2">
                        <span>Ответ для @{replyTo.username}</span>
                        <button onClick={() => setReplyTo(null)} className="text-gray-500 hover:text-white">Отмена</button>
                      </div>
                    )}
                    <input
                      id={`comment-${post.id}`}
                      type="text"
                      placeholder={replyTo?.postId === post.id ? `Ответить @${replyTo.username}...` : 'Написать комментарий...'}
                      value={commentInputs[post.id] || ''}
                      onChange={(e) => setCommentInputs({...commentInputs, [post.id]: e.target.value})}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddComment(post.id)}
                      className="w-full bg-gray-900/50 border border-gray-700 rounded-full px-4 py-1.5 text-sm text-white focus:border-blue-500 outline-none"
                    />
                  </div>
                  <button onClick={() => handleAddComment(post.id)} className="text-blue-400 hover:text-blue-300 text-sm font-bold px-2">Отправить</button>
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default FeedPage;
